// src/prototype/data/execute/pmoHandoff.ts
// ============================================================
// PMO HANDOFF KIT (E6, Oct 21) — charter, RACI, measurement plan and the downloadable kit.
//
// The measurement plan is the "outcome" half of the decision -> outcome record. Each metric
// carries a Value STEPS category (Satisfaction, Treatment, Efficiency, Prevention, Savings) so
// the record can later be made compatible with the HIMSS AI Outcomes Framework.
//
// NO FABRICATED NUMBERS: baselines and targets are set at kickoff from the client's own
// history. Until then they read "Set at kickoff", never an authored figure.
// ============================================================

import * as XLSX from 'xlsx';
import type { DecisionBrief } from '../decisionBrief';
import type { ExecMilestone, ExecRisk, TeamMember, InitiativeRecord } from '../executionInitiatives';
import type { RequirementVM } from '../adapters';

export const VALUE_STEPS = ['Satisfaction', 'Treatment', 'Efficiency', 'Prevention', 'Savings'] as const;
export type ValueStep = (typeof VALUE_STEPS)[number];
/** Citation shown wherever the Value STEPS tags appear. */
export const VALUE_STEPS_CITATION = 'Category scheme: HIMSS Value STEPS (Satisfaction, Treatment/Clinical, Electronic information/Efficiency, Prevention/Patient education, Savings).';

export interface MeasurementMetric {
  metric: string;
  valueStep: ValueStep;
  baselineDefinition: string;
  baselineValue: string;
  target: string;
  measurementDates: string;
  dataSource: string;
}

const KICKOFF = 'Set at kickoff';
const CADENCE = 'Baseline at kickoff · quarterly review · 12-month readout';

const AUTHORED_METRICS: Record<string, MeasurementMetric[]> = {
  'opp-1': [
    { metric: '30-day all-cause readmission rate', valueStep: 'Treatment', baselineDefinition: 'Trailing 12 months, index inpatient discharges', baselineValue: KICKOFF, target: KICKOFF, measurementDates: CADENCE, dataSource: 'EHR encounters; payer claims for out-of-network returns' },
    { metric: 'Post-discharge follow-up within 7 days', valueStep: 'Prevention', baselineDefinition: 'Share of discharges with a completed follow-up contact or visit', baselineValue: KICKOFF, target: KICKOFF, measurementDates: CADENCE, dataSource: 'EHR scheduling and care management records' },
    { metric: 'ED revisits within 30 days of discharge', valueStep: 'Prevention', baselineDefinition: 'Trailing 12 months, same index population', baselineValue: KICKOFF, target: KICKOFF, measurementDates: CADENCE, dataSource: 'EHR encounters; ADT notifications' },
    { metric: 'Post-acute spend per episode', valueStep: 'Savings', baselineDefinition: 'Risk-contract attributed episodes, trailing 12 months', baselineValue: KICKOFF, target: KICKOFF, measurementDates: CADENCE, dataSource: 'Payer claims; finance episode reporting' },
    { metric: 'Care manager time per transition', valueStep: 'Efficiency', baselineDefinition: 'Time study or workflow timestamps over 4 weeks', baselineValue: KICKOFF, target: KICKOFF, measurementDates: CADENCE, dataSource: 'Care management application timestamps' },
    { metric: 'Patient-reported care transition experience', valueStep: 'Satisfaction', baselineDefinition: 'Care transition items on the patient experience survey', baselineValue: KICKOFF, target: KICKOFF, measurementDates: CADENCE, dataSource: 'Patient experience survey vendor' },
  ],
};

/** Measurement plan for a use case: authored where reviewed, otherwise a starter plan from the brief. */
export function getMeasurementPlan(brief: DecisionBrief): { metrics: MeasurementMetric[]; reviewed: boolean } {
  const authored = AUTHORED_METRICS[brief.id];
  if (authored) return { metrics: authored, reviewed: true };
  return {
    reviewed: false,
    metrics: [
      { metric: `Primary outcome: ${brief.opImpact}`, valueStep: 'Treatment', baselineDefinition: 'Agree definition at kickoff', baselineValue: KICKOFF, target: KICKOFF, measurementDates: CADENCE, dataSource: 'To be confirmed at kickoff' },
      { metric: 'Annual financial benefit realized', valueStep: 'Savings', baselineDefinition: 'Finance-agreed method, consistent with the ROI model', baselineValue: KICKOFF, target: KICKOFF, measurementDates: CADENCE, dataSource: 'Finance reporting' },
      { metric: 'Staff time on the affected workflow', valueStep: 'Efficiency', baselineDefinition: 'Time study or workflow timestamps over 4 weeks', baselineValue: KICKOFF, target: KICKOFF, measurementDates: CADENCE, dataSource: 'Workflow system timestamps' },
    ],
  };
}

export const RACI_ROLES = [
  'Executive sponsor',
  'Initiative owner',
  'PMO lead',
  'Service-line and budget owner',
  'Clinical champion',
  'Application owner',
] as const;

/** R = responsible, A = accountable, C = consulted, I = informed. A starter matrix; edit at kickoff. */
export const RACI: { activity: string; codes: string[] }[] = [
  { activity: 'Charter approval', codes: ['A', 'R', 'C', 'C', 'C', 'I'] },
  { activity: 'Data access and baseline', codes: ['I', 'A', 'C', 'C', 'C', 'R'] },
  { activity: 'Build and configuration', codes: ['I', 'A', 'C', 'I', 'C', 'R'] },
  { activity: 'Clinical workflow change', codes: ['I', 'A', 'C', 'C', 'R', 'C'] },
  { activity: 'Go-live readiness', codes: ['I', 'A', 'R', 'C', 'C', 'C'] },
  { activity: 'Benefit measurement and reporting', codes: ['I', 'A', 'R', 'C', 'C', 'I'] },
];

export interface CharterSummary {
  problem: string;
  scope: string[];
  outOfScope: string;
  sponsor: string;
}

export function getCharter(brief: DecisionBrief, sponsor: string): CharterSummary {
  return {
    problem: brief.description,
    scope: brief.steps.length ? brief.steps : [brief.summary],
    outOfScope: 'To be agreed at kickoff',
    sponsor,
  };
}

export interface HandoffKitInput {
  brief: DecisionBrief;
  clientName: string;
  charter: CharterSummary;
  milestones: ExecMilestone[];
  risks: ExecRisk[];
  requirements: RequirementVM[];
  team: TeamMember[];
  metrics: MeasurementMetric[];
  handoff: { pmoOwner: string; handoffDate: string; status: string; roles: Record<string, string> };
  scoresAsOf: string;
}

function sheet(rows: (string | number)[][]) {
  const ws = XLSX.utils.aoa_to_sheet(rows);
  ws['!cols'] = rows[0]?.map((_, i) => ({ wch: Math.min(60, Math.max(14, ...rows.map((r) => String(r[i] ?? '').length))) }));
  return ws;
}

/** Builds the PMO handoff workbook (one sheet per artifact). Pure: returns the workbook. */
export function buildHandoffWorkbook(k: HandoffKitInput): XLSX.WorkBook {
  const wb = XLSX.utils.book_new();
  const header = [`${k.clientName} · ${k.brief.title}`, `Scores as of ${k.scoresAsOf}`];
  XLSX.utils.book_append_sheet(wb, sheet([
    header,
    [],
    ['Problem', k.charter.problem],
    ['Executive sponsor', k.charter.sponsor],
    ['In scope', k.charter.scope.join('\n')],
    ['Out of scope', k.charter.outOfScope],
    [],
    ['PMO owner', k.handoff.pmoOwner || 'To be assigned'],
    ['Handoff date', k.handoff.handoffDate || 'Not set'],
    ['Handoff status', k.handoff.status],
    ...Object.entries(k.handoff.roles).map(([role, name]) => [role, name || 'To be assigned']),
  ]), 'Charter');
  XLSX.utils.book_append_sheet(wb, sheet([['Activity', ...RACI_ROLES], ...RACI.map((r) => [r.activity, ...r.codes])]), 'RACI');
  XLSX.utils.book_append_sheet(wb, sheet([
    ['Milestone', 'Phase', 'Owner', 'Due', 'Status', '% complete', 'Blocker'],
    ...k.milestones.map((m) => [m.title, m.phase, m.owner, m.due, m.status, m.pct, m.blocker ?? '']),
  ]), 'Milestone plan');
  XLSX.utils.book_append_sheet(wb, sheet([
    ['Risk', 'Severity', 'Likelihood', 'Owner', 'Status', 'Description', 'Mitigation'],
    ...k.risks.map((r) => [r.title, r.severity, r.likelihood, r.owner, r.status, r.description, r.mitigation]),
  ]), 'Risk log');
  XLSX.utils.book_append_sheet(wb, sheet([
    ['Type', 'Requirement', 'Details'],
    ...(k.requirements.length ? k.requirements.map((r) => [r.type, r.name, r.details]) : [['', 'To be documented at kickoff', '']]),
  ]), 'Requirements');
  XLSX.utils.book_append_sheet(wb, sheet([
    ['Metric', 'Value STEPS category', 'Baseline definition', 'Baseline value', 'Target', 'Measurement dates', 'Data source'],
    ...k.metrics.map((m) => [m.metric, m.valueStep, m.baselineDefinition, m.baselineValue, m.target, m.measurementDates, m.dataSource]),
    [],
    [VALUE_STEPS_CITATION],
  ]), 'Measurement plan');
  XLSX.utils.book_append_sheet(wb, sheet([
    ['Role', 'Title', 'Name'],
    ...k.team.map((t) => [t.role, t.title, t.name ?? '']),
  ]), 'Team');
  return wb;
}

export function downloadHandoffWorkbook(k: HandoffKitInput): void {
  const slug = k.brief.title.replace(/[^A-Za-z0-9]+/g, '_').replace(/^_|_$/g, '');
  XLSX.writeFile(buildHandoffWorkbook(k), `${slug}_PMO_Handoff_Kit.xlsx`);
}

/** Kickoff deck outline, as plain text (one slide per heading). */
export function kickoffOutline(k: HandoffKitInput): string {
  return [
    `${k.clientName} · ${k.brief.title}: kickoff outline`,
    '',
    '1. Why we are doing this: the problem and the approved value range',
    '2. Scope and out of scope',
    '3. Roles: sponsor, initiative owner, PMO lead, service-line and budget owner, clinical champion, application owner',
    `4. Milestone plan (${k.milestones.length} milestones) and the first 30 days`,
    '5. Requirements: data, technology, people, governance',
    `6. Risks to watch (${k.risks.length} logged) and mitigations`,
    `7. Measurement plan: ${k.metrics.length} metrics, baseline method, review cadence`,
    '8. Decisions needed today and next steps',
  ].join('\n');
}

export function downloadText(filename: string, text: string): void {
  const blob = new Blob([text], { type: 'text/plain;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

// ---- Handoff defaults ------------------------------------------------------------------------

export interface HandoffDefaults {
  pmoOwner: string;
  handoffDate: string; // yyyy-mm-dd or ''
  status: 'Not started' | 'Handed off' | 'Accepted by PMO';
  roles: { 'Service-line and budget owner': string; 'Clinical champion': string; 'Application owner': string };
}

function isoFrom(due: string | undefined): string {
  if (!due) return '';
  const d = new Date(due);
  if (Number.isNaN(d.getTime())) return '';
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
}

const fmtMember = (t?: TeamMember) => (t ? (t.name ? `${t.name}, ${t.title}` : t.title) : '');

/**
 * Pre-filled handoff record for the demo, derived from the initiative record and its team
 * (fictional ACME roles). The user can overwrite any field; edits are stored separately.
 */
export function getHandoffDefaults(record: InitiativeRecord): HandoffDefaults {
  const team = record.team;
  const find = (re: RegExp) => team.find((t) => re.test(`${t.role} ${t.title}`));
  const kickoff = record.milestones.find((m) => /kickoff/i.test(m.title)) ?? record.milestones[0];
  const status: HandoffDefaults['status'] =
    record.status === 'Active' ? 'Accepted by PMO' : /kickoff/i.test(record.approval.find((a) => a.state === 'current')?.label ?? '') ? 'Handed off' : 'Not started';
  const byId: Record<string, Partial<HandoffDefaults['roles']>> = {
    'opp-1': { 'Service-line and budget owner': `${record.sponsor} (executive sponsor)`, 'Clinical champion': fmtMember(find(/care management|operational/i)), 'Application owner': fmtMember(find(/informatics|integration/i)) },
    'opp-2': { 'Service-line and budget owner': fmtMember(find(/pharmacy|initiative owner/i)), 'Clinical champion': fmtMember(find(/medical|clinical/i)), 'Application owner': 'Director, Ambulatory Applications (Epic Healthy Planet)' },
    'opp-3': { 'Service-line and budget owner': fmtMember(find(/ambulatory|initiative owner/i)), 'Clinical champion': fmtMember(find(/medical|clinical/i)), 'Application owner': 'Director, Access Applications (Epic Cadence and Referrals)' },
  };
  const roles = {
    'Service-line and budget owner': fmtMember(find(/initiative owner/i)) || record.sponsor,
    'Clinical champion': fmtMember(find(/medical|clinical|nurs/i)),
    'Application owner': fmtMember(find(/informatics|integration|application/i)),
    ...(byId[record.id] ?? {}),
  } as HandoffDefaults['roles'];
  return {
    pmoOwner: 'Senior Program Manager, Enterprise PMO',
    handoffDate: status === 'Not started' ? '' : isoFrom(kickoff?.due),
    status,
    roles,
  };
}
